import { mix } from '../icons/pix';
import { Layer } from './grid';
import { fist48, palm48 } from './hands48';

/** What a scene is drawn with: the element's tones plus the fixed material colours. */
export interface Tones {
  hi: string;
  l: string;
  c: string;
  d: string;
}
export interface Ctx {
  t: Tones;
  /** Variant number inside a scene (0 default). */
  v: number;
}

export const M = {
  steel: '#eef3fa',
  steelMid: '#a9b6c8',
  steelDark: '#66758d',
  skin: '#f4c9a0',
  skinDark: '#c48458',
  cloth: '#f4f1ea',
  red: '#dc4430',
  redDark: '#7a1520',
  gold: '#f6cf4a',
  goldDark: '#a8721c',
  wood: '#8a5a3a',
  woodDark: '#4a3024',
  bone: '#efe6d2',
  boneDark: '#b8a888',
  blood: '#e02e3a',
  bloodDark: '#7a0e16',
  ink: '#16130f',
  yellow: '#ffe45a',
  ice: '#cdefff',
  iceDark: '#5aa8d8',
  violet: '#c08aff',
  violetDark: '#6a3aa8',
  jade: '#55cf72',
  poison: '#9ce04a',
  fire: '#ff8a2a',
};

export const keyOf = (x: Ctx): Record<string, string> => ({
  k: M.skin,
  K: M.skinDark,
  w: M.cloth,
  r: M.red,
  R: M.redDark,
  g: M.gold,
  G: M.goldDark,
  W: M.steel,
  s: M.steelMid,
  S: M.steelDark,
  e: x.t.l,
  E: x.t.c,
  a: x.t.hi,
  D: x.t.d,
  b: M.wood,
  B: M.woodDark,
  p: M.bone,
  P: M.boneDark,
  y: M.yellow,
  i: M.ink,
  j: M.jade,
  f: M.fire,
});

/* ---------- primitives ---------- */

function unit(x0: number, y0: number, x1: number, y1: number): [number, number, number] {
  const len = Math.hypot(x1 - x0, y1 - y0) || 1;
  return [(x1 - x0) / len, (y1 - y0) / len, len];
}

/** A straight sword from its pommel to its tip. */
export function sword(L: Layer, x0: number, y0: number, x1: number, y1: number, blade = M.steel, grip = M.wood): void {
  const [ux, uy, len] = unit(x0, y0, x1, y1);
  const gx = x0 + ux * 3.5;
  const gy = y0 + uy * 3.5;
  L.line(x0, y0, gx, gy, grip, 1);
  L.line(gx + uy * 2.4, gy - ux * 2.4, gx - uy * 2.4, gy + ux * 2.4, M.gold, 1);
  L.line(gx + ux * 1.5, gy + uy * 1.5, x1 - ux * 1.2, y1 - uy * 1.2, blade, 2);
  L.set(x1, y1, blade);
  L.set(x0, y0, M.goldDark);
  void len;
}

/** A curved sabre: the blade bows to the cutting side. */
export function sabre(L: Layer, x0: number, y0: number, x1: number, y1: number, blade = M.steel, bow = 2.5): void {
  const [ux, uy] = unit(x0, y0, x1, y1);
  const gx = x0 + ux * 3.5;
  const gy = y0 + uy * 3.5;
  L.line(x0, y0, gx, gy, M.wood, 1);
  L.disc(gx, gy, 1.4, M.gold);
  const mx = (gx + x1) / 2 + uy * bow;
  const my = (gy + y1) / 2 - ux * bow;
  L.curve(gx + ux, gy + uy, mx, my, x1, y1, blade, 2);
  L.set(x0, y0, M.goldDark);
}

/** A tapered crescent slash trail along a circle. */
export function crescent(L: Layer, cx: number, cy: number, r: number, a0: number, a1: number, c1: string, c2 = c1): void {
  for (let k = 0; k < 3; k++) {
    const shrink = (a1 - a0) * 0.1 * k;
    L.arc(cx, cy, r - k, a0 + shrink, a1 - shrink, k === 0 ? c1 : c2);
  }
}

export function spark(L: Layer, x: number, y: number, r: number, c: string, core = '#ffffff'): void {
  if (L.detail === 0) return;
  L.set(x, y, core);
  for (let i = 1; i <= r; i++) {
    L.set(x + i, y, c);
    L.set(x - i, y, c);
    L.set(x, y + i, c);
    L.set(x, y - i, c);
  }
}

export function dot(L: Layer, x: number, y: number, c: string): void {
  if (L.detail === 0) return;
  L.set(x, y, c);
}

export function speedLines(L: Layer, pts: ReadonlyArray<readonly [number, number, number]>, c: string): void {
  for (const [x, y, n] of pts) L.line(x, y, x - n, y, c, 1);
}

/** Starburst: rays from a point. */
export function burst(L: Layer, cx: number, cy: number, r0: number, r1: number, n: number, c: string, c2 = c, a0 = 0): void {
  for (let i = 0; i < n; i++) {
    const a = a0 + (i / n) * Math.PI * 2;
    const rr = i % 2 ? r1 - 2 : r1;
    L.line(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0, cx + Math.cos(a) * rr, cy + Math.sin(a) * rr, i % 2 ? c2 : c);
  }
}

export function flake(L: Layer, cx: number, cy: number, r: number, c: string, c2 = c): void {
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
    const ex = cx + Math.cos(a) * r;
    const ey = cy + Math.sin(a) * r;
    L.line(cx, cy, ex, ey, c);
    if (r >= 4) {
      const bx = cx + Math.cos(a) * r * 0.6;
      const by = cy + Math.sin(a) * r * 0.6;
      L.line(bx, by, bx + Math.cos(a + 1) * 1.6, by + Math.sin(a + 1) * 1.6, c2);
      L.line(bx, by, bx + Math.cos(a - 1) * 1.6, by + Math.sin(a - 1) * 1.6, c2);
    }
  }
  L.set(cx, cy, '#ffffff');
}

export function drop(L: Layer, x: number, y: number, c: string, hi = '#ffb0b0'): void {
  L.set(x, y - 1, c);
  L.rect(x - 1, y, 3, 2, c);
  L.set(x, y + 2, c);
  L.set(x + 1, y, hi);
}

export function cloud(L: Layer, x: number, y: number, w: number, c: string, shade: string): void {
  L.rect(x, y + 2, w, 2, c);
  L.rect(x + 1, y + 1, w - 2, 1, c);
  L.rect(x + 2, y, Math.max(2, w - 5), 1, c);
  L.rect(x, y + 4, w, 1, shade);
}

export function yinyang(L: Layer, cx: number, cy: number, r: number, dark = M.ink, light = M.cloth): void {
  const k = L.k;
  const X = (cx + 0.5) * k - 0.5;
  const Y = (cy + 0.5) * k - 0.5;
  const R = r * k;
  for (let y = Math.floor(Y - R); y <= Math.ceil(Y + R); y++)
    for (let x = Math.floor(X - R); x <= Math.ceil(X + R); x++) {
      const dx = x - X;
      const dy = y - Y;
      if (dx * dx + dy * dy > R * R + 0.3 * k) continue;
      const dTop = dx * dx + (dy + R / 2) ** 2 <= (R / 2) ** 2;
      const dBot = dx * dx + (dy - R / 2) ** 2 <= (R / 2) ** 2;
      let c: string;
      if (dx >= 0) c = dBot ? dark : light;
      else c = dTop ? light : dark;
      if (dx * dx + (dy + R / 2) ** 2 <= (R / 6) ** 2 + 0.2) c = dark;
      if (dx * dx + (dy - R / 2) ** 2 <= (R / 6) ** 2 + 0.2) c = light;
      L.raw(x, y, c);
    }
}

/* ---------- sprites ---------- */

const CLAW = [
  'p..p..p..p',
  'kp.kp.kp.k',
  'kkkkkkkkkk',
  'kkkkkkkkkK',
  '.kkkkkkkK.',
  '..wwwwww..',
];
const BELL = [
  '....gg....',
  '...gGGg...',
  '..gggggg..',
  '.ggggggGg.',
  '.gggggggG.',
  '.gggggggG.',
  'gggggggggG',
  'GGGGGGGGGG',
  '...GggG...',
];
const FANG_TOP = ['pPpPpPp', 'ppppppp', '.p.p.p.'];

/* ---------- scenes ---------- */

export type Scene = (L: Layer, x: Ctx) => void;

/** A fist seen knuckles-on: four curled fingers, a thumb band, a gold bracer. */
export function fistHand(L: Layer, x0: number, y0: number): void {
  if (L.k >= 2) return fist48(L, Math.round(x0 * L.k), Math.round(y0 * L.k) - 2);
  L.rect(x0 + 1, y0, 10, 7, M.skin);
  L.rect(x0, y0 + 1, 12, 5, M.skin);
  for (const dx of [3, 6, 9]) L.rect(x0 + dx, y0 + 1, 1, 4, M.skinDark);
  L.rect(x0 + 1, y0 + 5, 7, 1, M.skinDark);
  L.rect(x0 + 1, y0 + 6, 5, 2, M.skinDark);
  L.rect(x0 + 2, y0 + 8, 8, 2, M.gold);
  L.rect(x0 + 2, y0 + 10, 8, 1, M.goldDark);
}

/** An open palm pushed out: four fingers with dark seams, a thumb, a gold bracer. */
export function palmHand(L: Layer, x0: number, y0: number): void {
  if (L.k >= 2) return palm48(L, Math.round(x0 * L.k) + 2, Math.round(y0 * L.k) - 8);
  const hs = [5, 7, 8, 6];
  hs.forEach((h, i) => {
    L.rect(x0 + 2 + i * 3, y0 + 8 - h, 2, h + 1, M.skin);
    L.rect(x0 + 4 + i * 3, y0 + 8 - h + 1, 1, h, M.skinDark);
  });
  L.rect(x0 + 1, y0 + 6, 12, 5, M.skin);
  L.rect(x0 - 1, y0 + 5, 3, 3, M.skin);
  L.rect(x0 + 2, y0 + 10, 9, 1, M.skinDark);
  L.rect(x0 + 2, y0 + 11, 9, 2, M.gold);
}

const handFx = (L: Layer, _x: Ctx, kind: 'fist' | 'palm', ox: number, oy: number) => {
  if (kind === 'fist') fistHand(L, ox, oy);
  else palmHand(L, ox, oy);
};

export const SCENES: Record<string, Scene> = {
  bolt: (L, x) => {
    sword(L, 3, 20, 14, 9);
    L.poly([[19, 2], [14, 9], [18, 10], [12, 18]], M.yellow, 2);
    L.poly([[19, 2], [14, 9], [18, 10], [12, 18]], '#ffffff', 1);
    spark(L, 20, 13, 2, x.t.l);
    spark(L, 8, 8, 1, x.t.l);
  },
  pierce: (L, x) => {
    speedLines(L, [[10, 8, 5], [12, 16, 6], [9, 20, 4]], x.t.l);
    sword(L, 2, 12, 19, 12);
    spark(L, 20, 12, 3, x.t.hi);
  },
  nine: (L, x) => {
    const angs = [-1.0, -0.5, 0, 0.5, 1.0];
    angs.forEach((a, i) => {
      const ex = 12 + Math.sin(a) * 11;
      const ey = 19 - Math.cos(a) * 15;
      L.line(12, 19, ex, ey, i === 2 ? M.steel : M.steelMid, i === 2 ? 2 : 1);
      L.set(ex, ey, '#ffffff');
    });
    L.rect(10, 19, 5, 1, M.gold);
    spark(L, 12, 20, 1, x.t.hi);
  },
  stars: (L, x) => {
    const pts: Array<[number, number]> = [[5, 17], [8, 15], [11, 14], [13, 10], [17, 9], [19, 5], [16, 5]];
    L.poly(pts, x.t.d);
    pts.forEach(([px, py], i) => spark(L, px, py, i === 3 ? 2 : 1, x.t.l, M.steel));
  },
  needles: (L, x) => {
    const n = 3 + (x.v % 3);
    for (let i = 0; i < n; i++) {
      const a = -0.9 + (i * 1.2) / Math.max(1, n - 1);
      const ex = 5 + Math.cos(a) * 15;
      const ey = 17 + Math.sin(a) * 15;
      L.line(5, 17, ex, ey, M.steelMid, 1);
      L.set(ex, ey, x.t.hi);
      L.set(ex - 1, ey + (a > 0 ? -1 : 1) * 0, M.steel);
    }
    L.disc(5, 17, 1.5, M.gold);
  },
  palm: (L, x) => {
    handFx(L, x, 'palm', 3, 8);
    L.arc(8, 13, 8, -0.9, 0.9, x.t.l);
    L.arc(8, 13, 10, -0.8, 0.8, x.t.c);
    spark(L, 19, 5, 1, x.t.hi);
  },
  vajra: (L, x) => {
    if (x.v === 1) handFx(L, x, 'palm', 6, 8);
    else if (x.v === 2) {
      // a stepping foot
      L.rect(5, 15, 10, 3, M.cloth);
      L.rect(5, 18, 10, 2, M.woodDark);
      L.rect(13, 12, 3, 3, M.cloth);
    } else handFx(L, x, 'fist', 6, 8);
    burst(L, 12, 12, 8, 11, 8, M.gold, M.goldDark);
    L.arc(12, 12, 8, -2.6, -0.5, M.gold);
  },
  grab: (L, x) => {
    L.sprite(7, 6, CLAW, keyOf(x));
    L.arc(12, 16, 7, 0.4, 2.7, x.t.l);
    L.arc(12, 16, 9, 0.6, 2.5, x.t.c);
    spark(L, 19, 5, 1, x.t.hi);
  },
  whirl: (L, x) => {
    const a0 = x.v * 0.5;
    L.arc(12, 12, 8, a0, a0 + 4.2, x.t.l, 2);
    L.arc(12, 12, 5, a0 + 2.5, a0 + 6.0, x.t.c, 1);
    L.arc(12, 12, 8, a0 + 4.2, a0 + 5.2, x.t.hi);
    L.rect(11, 11, 2, 2, M.steel);
    spark(L, 20, 6, 1, x.t.hi);
    spark(L, 4, 18, 1, x.t.hi);
  },
  wind: (L, x) => {
    L.curve(3, 8, 11, 3, 17, 8, x.t.l, 2);
    L.arc(17, 10, 2, -1.6, 1.8, x.t.l);
    L.curve(4, 14, 13, 10, 20, 14, x.t.c, 1);
    L.curve(3, 19, 10, 16, 15, 19, x.t.l, 1);
    L.arc(15, 20, 2, -1.2, 2.2, x.t.l);
    dot(L, 20, 5, x.t.hi);
    dot(L, 6, 5, x.t.hi);
  },
  breath: (L, x) => {
    L.curve(12, 20, 5, 15, 12, 11, x.t.l, 2);
    L.curve(12, 11, 18, 8, 12, 4, x.t.l, 2);
    L.curve(15, 20, 19, 15, 16, 13, x.t.c, 1);
    spark(L, 6, 7, 1, x.t.hi);
    spark(L, 18, 17, 1, x.t.hi);
    dot(L, 12, 3, x.t.hi);
  },
  taiji: (L, x) => {
    yinyang(L, 12, 12, 6);
    L.arc(12, 12, 8.5, -0.3, 1.9, x.t.l);
    L.arc(12, 12, 8.5, 2.8, 5.0, x.t.l);
    spark(L, 19, 5, 1, x.t.hi);
  },
  calm: (L, x) => {
    L.fillPoly([[12, 5], [9, 11], [12, 16], [15, 11]], x.t.hi);
    L.fillPoly([[4, 9], [8, 12], [11, 16], [6, 16]], x.t.l);
    L.fillPoly([[20, 9], [16, 12], [13, 16], [18, 16]], x.t.l);
    L.fillPoly([[5, 17], [19, 17], [16, 19], [8, 19]], x.t.c);
    L.line(4, 21, 19, 21, x.t.d);
    spark(L, 12, 3, 1, x.t.hi);
    dot(L, 20, 6, x.t.hi);
  },
  aura: (L, x) => {
    L.arc(12, 13, 8, 0, 6.3, x.t.c, 2);
    L.arc(12, 13, 8, -2.4, -0.2, x.t.hi);
    for (let i = 0; i < 3; i++) L.poly([[8, 17 - i * 4], [12, 13 - i * 4], [16, 17 - i * 4]], i === 0 ? x.t.hi : i === 1 ? x.t.l : x.t.c, 1);
    spark(L, 19, 4, 1, x.t.hi);
  },
  chain: (L, x) => {
    const H = ['.ssss.', 'sWWWWs', 's....s', 's....s', 'SsssSS'];
    const V = ['.sss.', 'sWWWs', 'sW.Ss', 'sW.Ss', 'sW.Ss', 'sSSSs', '.SSS.'];
    const k = { s: M.steelMid, S: M.steelDark, W: M.steel };
    L.sprite(2, 15, H, k);
    L.sprite(6, 11, V, k);
    L.sprite(9, 8, H, k);
    L.sprite(13, 3, V, k);
    L.line(15, 15, 21, 21, x.t.l, 1);
    spark(L, 19, 17, 1, x.t.hi);
  },
  crush: (L, x) => {
    burst(L, 12, 12, 3, 10, 10, x.t.hi, x.t.l);
    L.disc(12, 12, 4, M.bone);
    L.disc(13, 11, 2, '#ffffff');
    L.rect(5, 19, 3, 2, M.boneDark);
    L.rect(17, 19, 3, 2, M.boneDark);
  },
  quake: (L, x) => {
    handFx(L, x, 'fist', 7, 3);
    L.poly([[12, 12], [10, 15], [13, 17], [9, 21]], x.t.hi, 1);
    L.poly([[12, 12], [15, 15], [14, 18], [19, 20]], x.t.hi, 1);
    L.rect(3, 19, 4, 3, x.t.d);
    L.rect(17, 20, 3, 2, x.t.d);
    L.rect(5, 15, 2, 2, x.t.c);
    L.rect(18, 14, 2, 2, x.t.c);
  },
  cleave: (L, x) => {
    crescent(L, 6, 18, 14, -1.45, 0.05, x.t.hi, x.t.l);
    crescent(L, 6, 18, 11, -1.4, -0.1, x.t.c, x.t.d);
    sword(L, 3, 21, 11, 13);
    spark(L, 19, 17, 2, x.t.hi);
  },
  sabre: (L, x) => {
    crescent(L, 12, 19, 11, -2.7, -0.7, x.t.l, x.t.c);
    sabre(L, 4, 20, 19, 5);
    spark(L, 20, 4, 2, x.t.hi);
  },
  slash: (L, x) => {
    for (let i = 0; i < 3; i++) {
      const o = i * 5;
      L.curve(3 + o, 3, 7 + o, 12, 6 + o, 21, x.t.hi, 2);
      L.curve(4 + o, 3, 8 + o, 12, 7 + o, 21, x.t.l, 1);
    }
    drop(L, 20, 19, M.blood);
  },
  bite: (L, x) => {
    // maw: upper and lower jaw with teeth
    L.rect(4, 6, 16, 3, M.redDark);
    L.rect(4, 15, 16, 3, M.redDark);
    L.rect(5, 9, 14, 6, M.bloodDark);
    for (let i = 0; i < 4; i++) {
      L.rect(5 + i * 4, 9, 2, 3, M.bone);
      L.set(5 + i * 4, 12, M.boneDark);
      L.rect(7 + i * 4, 12, 2, 3, M.bone);
    }
    L.rect(7, 5, 3, 1, x.t.hi);
    L.rect(14, 18, 3, 1, x.t.hi);
    void FANG_TOP;
  },
  blood: (L, x) => {
    crescent(L, 12, 19, 11, -2.7, -0.7, M.blood, M.bloodDark);
    sabre(L, 4, 20, 19, 5, mix(M.steel, M.blood, 0.35));
    drop(L, 18, 14, M.blood);
    drop(L, 5, 8, M.blood);
    drop(L, 12, 18, M.bloodDark, M.blood);
    void x;
  },
  bloodstorm: (L, x) => {
    L.arc(12, 12, 8, 0.4, 4.6, M.blood, 2);
    L.arc(12, 12, 5, 3, 6.4, M.bloodDark, 1);
    drop(L, 12, 11, M.blood);
    drop(L, 19, 15, M.blood);
    drop(L, 5, 6, M.blood);
    void x;
  },
  shadow: (L, x) => {
    L.disc(16, 7, 5, M.violet);
    L.disc(18, 6, 4.2, null);
    const RUN = ['..kk..', '..kk..', '.DDDD.', 'DDDDDD', '.DDDD.', '.D..D.', 'D....D'];
    const g1 = mix(x.t.d, M.ink, 0.5);
    const g2 = mix(x.t.d, M.ink, 0.2);
    L.sprite(2, 12, RUN, { k: g1, D: g1 });
    L.sprite(6, 12, RUN, { k: g2, D: g2 });
    L.sprite(10, 12, RUN, { k: M.skin, D: x.t.l });
    L.line(15, 15, 21, 11, M.steel, 1);
    spark(L, 4, 6, 1, x.t.hi);
  },
  brush: (L, x) => {
    L.curve(4, 19, 8, 12, 16, 14, x.t.d, 3);
    L.curve(4, 19, 8, 12, 16, 14, x.t.l, 1);
    L.line(15, 14, 20, 4, M.wood, 2);
    L.line(15, 14, 17, 10, M.cloth, 2);
    L.set(20, 4, M.gold);
    drop(L, 5, 21, x.t.d, x.t.l);
  },
  ink: (L, x) => {
    L.rect(5, 3, 14, 18, M.cloth);
    L.rect(4, 3, 1, 18, M.boneDark);
    L.rect(19, 3, 1, 18, M.boneDark);
    L.rect(5, 3, 14, 1, M.steelMid);
    L.disc(11, 11, 4, M.ink);
    L.disc(15, 8, 2, M.ink);
    L.disc(8, 16, 2, M.ink);
    L.disc(16, 16, 1.4, M.ink);
    L.set(10, 9, x.t.l);
    L.set(11, 9, x.t.l);
  },
  seal: (L, x) => {
    L.rect(5, 6, 14, 14, M.red);
    L.rect(6, 7, 12, 12, M.redDark);
    L.rect(7, 8, 10, 10, M.red);
    L.rect(8, 9, 8, 8, M.redDark);
    L.rect(10, 11, 4, 4, M.red);
    L.line(2, 4, 4, 6, x.t.hi);
    L.line(21, 4, 19, 6, x.t.hi);
    L.line(2, 22, 4, 20, x.t.hi);
    L.line(21, 22, 19, 20, x.t.hi);
    spark(L, 12, 3, 1, x.t.hi);
  },
  finger: (L, x) => {
    L.rect(3, 12, 8, 4, M.skin);
    L.rect(8, 10, 8, 2, M.skin);
    L.rect(15, 10, 2, 2, M.skin);
    L.line(18, 10, 22, 6, x.t.hi, 2);
    L.line(18, 11, 22, 7, x.t.l, 1);
    spark(L, 20, 6, 2, '#ffffff', x.t.hi);
  },
  snow: (L, x) => {
    flake(L, 12, 12, 8, M.ice, M.iceDark);
    sword(L, 3, 21, 9, 15);
    void x;
  },
  snowdash: (L, x) => {
    speedLines(L, [[10, 7, 6], [8, 12, 5], [10, 17, 6]], M.ice);
    sword(L, 3, 20, 20, 8, M.ice);
    flake(L, 16, 17, 3, M.ice);
    void x;
  },
  blizzard: (L, x) => {
    L.arc(12, 12, 8, 0.5, 4.8, M.ice, 2);
    flake(L, 7, 8, 2, '#ffffff');
    flake(L, 17, 8, 3, M.ice);
    flake(L, 15, 17, 2, '#ffffff');
    flake(L, 6, 17, 2, M.ice);
    void x;
  },
  snowstep: (L, x) => {
    L.rect(5, 13, 3, 5, M.ice);
    L.rect(5, 19, 3, 2, M.ice);
    L.rect(12, 7, 3, 5, M.ice);
    L.rect(12, 13, 3, 2, M.ice);
    flake(L, 18, 18, 3, M.ice);
    flake(L, 18, 6, 2, '#ffffff');
    void x;
  },
  icebreak: (L, x) => {
    L.poly([[12, 3], [9, 9], [12, 12], [8, 18], [11, 21]], M.ice, 2);
    L.poly([[12, 3], [16, 8], [13, 12], [17, 17]], M.iceDark, 1);
    L.rect(3, 14, 3, 3, M.iceDark);
    L.rect(18, 15, 3, 3, M.ice);
    L.rect(17, 5, 2, 2, M.ice);
    void x;
  },
  snowpride: (L, x) => {
    L.arc(12, 12, 8, 0, 6.3, M.ice, 2);
    flake(L, 12, 12, 5, '#ffffff', M.iceDark);
    void x;
  },
  snowblink: (L, x) => {
    flake(L, 12, 12, 8, '#ffffff', M.ice);
    L.disc(12, 12, 2, M.violet);
    spark(L, 19, 4, 2, M.ice);
    spark(L, 4, 19, 2, M.ice);
    void x;
  },
  bell: (L, x) => {
    L.sprite(4, 6, BELL, keyOf(x));
    L.arc(8, 11, 6, -0.9, 0.9, x.t.l);
    L.arc(8, 11, 9, -0.8, 0.8, x.t.c);
    L.arc(14, 11, 8, 3.8, 5.5, x.t.l);
    L.rect(4, 17, 2, 2, M.goldDark);
    spark(L, 19, 5, 1, x.t.hi);
  },
  ring: (L, x) => {
    L.arc(12, 12, 9, 0, 6.3, x.t.c, 1);
    L.arc(12, 12, 6, 0, 6.3, x.t.l, 1);
    burst(L, 12, 12, 1, 4, 8, x.t.hi, x.t.l);
    L.disc(12, 12, 1.5, '#ffffff');
  },
  dgHarpoon: (L, x) => {
    L.line(4, 20, 17, 7, M.wood, 2);
    L.poly([[16, 8], [20, 4]], M.steel, 2);
    L.poly([[17, 3], [21, 3], [21, 7]], M.steelMid, 1);
    L.curve(4, 20, 3, 12, 8, 8, x.t.l, 1);
  },
  dgSleep: (L, x) => {
    cloud(L, 4, 12, 14, x.t.l, x.t.d);
    cloud(L, 7, 8, 12, x.t.hi, x.t.c);
    L.poly([[16, 3], [20, 3], [16, 7], [20, 7]], M.cloth, 1);
    L.poly([[10, 17], [13, 17], [10, 20], [13, 20]], M.cloth, 1);
  },
  dgChain: (L, x) => {
    SCENES.chain!(L, x);
    L.disc(20, 3, 1.5, M.gold);
  },
  dgWhirlpool: (L, x) => {
    L.arc(12, 12, 9, 0.2, 4.6, '#7fd8ff', 2);
    L.arc(12, 12, 6, 2.2, 6.4, '#4aa8e8', 2);
    L.arc(12, 12, 3, 4, 8, '#bfeaff', 1);
    void x;
  },
  dgIron: (L, x) => {
    L.fillPoly([[5, 4], [19, 4], [19, 12], [12, 21], [5, 12]], M.steelMid);
    L.fillPoly([[7, 6], [17, 6], [17, 11], [12, 18], [7, 11]], M.steel);
    L.rect(11, 6, 2, 12, M.steelMid);
    L.rect(7, 10, 10, 2, M.steelMid);
    spark(L, 19, 20, 1, x.t.hi);
  },
};
